import React, { Component } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Button,
  Grid,
  Paper,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  DesktopWindows as DesktopIcon,
  PhoneIphone as MobileIcon,
  Layers as LayersIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Tune as TuneIcon
} from '@mui/icons-material';

class BannersConfig extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bannersAtivos: true,
      modoExibicao: 'carrossel',
      bannersList: [], // { id, file, previewUrl, name, isExisting }
      deletedBannerIds: [], // IDs já salvos no banco marcados para remoção ao clicar em salvar
      loading: false,
      fetching: true,
      snackbar: {
        open: false,
        message: '',
        severity: 'success'
      }
    };

    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.loadBannersConfig();
  }

  // Busca dados na API ao carregar a página
  // Carrega as configurações do servidor e monta a URL da foto
  loadBannersConfig = async () => {
    try {
      this.setState({ fetching: true });

      // Substitua pela variável onde fica o endereço base da sua API
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

      const response = await fetch(`${apiUrl}/banners`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        // Formata a lista de banners recebida do backend
        const formattedBanners = (data.banners || []).map((banner) => {
          // Pega o nome do arquivo recebido da API (ex: "9792f3a964023220943fe5d880126060.png")
          const imageName = banner.name || banner.previewUrl || banner.imagem;

          return {
            id: banner.id,
            name: imageName,
            // Monta o caminho apontando para a sua rota de fotos
            previewUrl: `${apiUrl}/store/photo/${imageName}`,
            isExisting: true
          };
        });

        this.setState({
          bannersAtivos: data.bannersAtivos,
          modoExibicao: data.modoExibicao,
          bannersList: formattedBanners,
          deletedBannerIds: []
        });
      } else {
        throw new Error('Erro ao carregar dados');
      }
    } catch (error) {
      console.error(error);
      this.setState({
        snackbar: { open: true, message: 'Erro ao carregar banners do servidor.', severity: 'error' }
      });
    } finally {
      this.setState({ fetching: false });
    }
  };

  handleToggleBanners = (event) => {
    this.setState({ bannersAtivos: event.target.checked });
  };

  handleModoExibicaoChange = (modo) => {
    this.setState({ modoExibicao: modo });
  };

  handleTriggerFileInput = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  // Selecionar novos arquivos
  handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const { bannersList } = this.state;

    if (files.length === 0) return;

    const limitAvailable = 5 - bannersList.length;
    const selectedFiles = files.slice(0, limitAvailable);

    const newBanners = selectedFiles.map((file) => ({
      id: 'new_' + Date.now() + '_' + Math.random(),
      file: file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      isExisting: false
    }));

    this.setState({
      bannersList: [...bannersList, ...newBanners]
    });

    event.target.value = null;
  };

  // Remove apenas visualmente da tela. O delete no banco só ocorre no handleSave
  handleRemoveBanner = (banner) => {
    this.setState((prevState) => {
      const updatedList = prevState.bannersList.filter((item) => item.id !== banner.id);
      const updatedDeletedIds = [...prevState.deletedBannerIds];

      if (banner.isExisting) {
        updatedDeletedIds.push(banner.id);
      }

      return {
        bannersList: updatedList,
        deletedBannerIds: updatedDeletedIds
      };
    });
  };

  // Salva e aplica exclusões/inserções de uma vez só sem recarregar a tela
  handleSave = async () => {
    const { bannersAtivos, modoExibicao, bannersList, deletedBannerIds } = this.state;

    this.setState({ loading: true });

    try {
      const formData = new FormData();
      formData.append('bannersAtivos', bannersAtivos);
      formData.append('modoExibicao', modoExibicao);
      formData.append('deletedBannerIds', JSON.stringify(deletedBannerIds));

      // Envia somente arquivos locais novos
      bannersList.forEach((banner) => {
        if (!banner.isExisting && banner.file) {
          formData.append('banners', banner.file);
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/banners`, {
        credentials: 'include',
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        this.setState({
          snackbar: { open: true, message: 'Configurações e banners atualizados com sucesso!', severity: 'success' }
        });
        // Atualiza a tela puxando o estado fresco sem recarregar o navegador
        await this.loadBannersConfig();
      } else {
        throw new Error('Falha ao salvar dados');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      this.setState({
        snackbar: { open: true, message: 'Erro ao salvar alterações.', severity: 'error' }
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleCloseSnackbar = () => {
    this.setState({ snackbar: { ...this.state.snackbar, open: false } });
  };

  render() {
    const { bannersAtivos, modoExibicao, bannersList, loading, fetching, snackbar } = this.state;

    if (fetching) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ margin: '0 auto', fontFamily: 'sans-serif' }}>
        <input
          type="file"
          ref={this.fileInputRef}
          onChange={this.handleFileChange}
          accept="image/png, image/jpeg, image/webp"
          multiple
          style={{ display: 'none' }}
        />

        {/* Cabeçalho */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              Banners
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Banners promocionais
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={this.handleSave}
            disabled={loading}
            sx={{ textTransform: 'none', px: 3, borderRadius: 2 }}
          >
            Salvar
          </Button>
        </Box>

        {/* Painel Principal */}
        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Stack spacing={3}>

            {/* Ativar Banners Switch */}
            <Paper
              variant="outlined"
              sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2 }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Ativar banners
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Exibe banners promocionais no catálogo
                </Typography>
              </Box>
              <Switch
                checked={bannersAtivos}
                onChange={this.handleToggleBanners}
                color="secondary"
              />
            </Paper>

            {/* Modo de Exibição */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                Modo de exibição
              </Typography>
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Card
                    variant="outlined"
                    onClick={() => this.handleModoExibicaoChange('sequencial')}
                    sx={{
                      cursor: 'pointer',
                      borderColor: modoExibicao === 'sequencial' ? '#8a2be2' : '#e0e0e0',
                      borderWidth: modoExibicao === 'sequencial' ? 2 : 1,
                      backgroundColor: modoExibicao === 'sequencial' ? '#fbf8ff' : '#fff',
                      textAlign: 'center',
                      py: 2.5
                    }}
                  >
                    <CardContent sx={{ p: '16px !important' }}>
                      <LayersIcon sx={{ color: modoExibicao === 'sequencial' ? '#8a2be2' : 'action.active', mb: 1 }} />
                      <Typography variant="subtitle2" fontWeight="bold" color={modoExibicao === 'sequencial' ? '#8a2be2' : 'text.primary'}>
                        Sequencial
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Banners em sequência
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Card
                    variant="outlined"
                    onClick={() => this.handleModoExibicaoChange('carrossel')}
                    sx={{
                      cursor: 'pointer',
                      borderColor: modoExibicao === 'carrossel' ? '#8a2be2' : '#e0e0e0',
                      borderWidth: modoExibicao === 'carrossel' ? 2 : 1,
                      backgroundColor: modoExibicao === 'carrossel' ? '#fbf8ff' : '#fff',
                      textAlign: 'center',
                      py: 2.5
                    }}
                  >
                    <CardContent sx={{ p: '16px !important' }}>
                      <LayersIcon sx={{ color: modoExibicao === 'carrossel' ? '#8a2be2' : 'action.active', mb: 1 }} />
                      <Typography variant="subtitle2" fontWeight="bold" color={modoExibicao === 'carrossel' ? '#8a2be2' : 'text.primary'}>
                        Carrossel
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Slider com navegação
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {modoExibicao === 'carrossel' && (
              <Accordion variant="outlined" sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TuneIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2">Configurações do carrossel</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    Configurações adicionais do slider.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Dimensões Recomendadas */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Dimensões recomendadas
              </Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item size={{ xs: 12, sm: 6 }} display="flex" alignItems="center" gap={1}>
                  <DesktopIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Desktop: 1600×500px
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }} display="flex" alignItems="center" gap={1}>
                  <MobileIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Mobile: 800×400px
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Contagem e Botão Adicionar */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Banners ativos: <strong>{bannersList.length}</strong> / 5
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={this.handleTriggerFileInput}
                disabled={bannersList.length >= 5 || !bannersAtivos || loading}
                sx={{ textTransform: 'none' }}
              >
                Adicionar
              </Button>
            </Box>

            {/* Lista de Banners */}
            {bannersList.length === 0 ? (
              <Box
                onClick={bannersAtivos ? this.handleTriggerFileInput : null}
                sx={{
                  border: '2px dashed #e0e0e0',
                  borderRadius: 2,
                  p: 5,
                  textAlign: 'center',
                  cursor: bannersAtivos ? 'pointer' : 'default'
                }}
              >
                <ImageIcon sx={{ fontSize: 48, color: '#c0c0c0', mb: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  Nenhum banner
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {bannersList.map((banner, index) => (
                  <Paper
                    key={banner.id}
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2 }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        component="img"
                        src={banner.previewUrl}
                        alt={`Banner ${index + 1}`}
                        sx={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 1 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Banner {index + 1}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {banner.name}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      color="error"
                      onClick={() => this.handleRemoveBanner(banner)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Paper>
                ))}
              </Stack>
            )}

          </Stack>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={this.handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default BannersConfig;